import { useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { MapPin } from 'lucide-react'
import { getEventImage, getLocationText, getFacilitatorNames, renderFacilitatorLinks } from '../../../utils/eventPresentation'
import { getMediaBaseUrl } from '../../../utils/mediaHelpers'
import { getEventCity, isFreeEvent, TYPE_OPTIONS } from '../../../utils/eventFilters'

const FORMAT_LABELS = { 'in-person': 'In person', online: 'Online', hybrid: 'Hybrid' }

const TYPE_LABELS = Object.fromEntries(TYPE_OPTIONS.map(({ value, label }) => [value, label]))

const caps = (d, opts) =>
  d.toLocaleDateString('en-IN', opts).replace(/,/g, '').toUpperCase()

// "SAT 9 MAY · 11:00 – 12:30" (single day) /
// "SAT 9 MAY — SAT 27 JUN · 11:00 – 12:30" (multi-day)
function formatCardDateLine(event) {
  const start = new Date(event.eventDate)
  const end = event.endDate ? new Date(event.endDate) : null
  const dayOpts = { weekday: 'short', day: 'numeric', month: 'short' }
  const timeOpts = { hour: '2-digit', minute: '2-digit', hour12: false }

  let datePart = caps(start, dayOpts)
  if (end && end.toDateString() !== start.toDateString()) {
    datePart += ` — ${caps(end, dayOpts)}`
  }

  let timePart = start.toLocaleTimeString('en-IN', timeOpts)
  if (end) timePart += ` – ${end.toLocaleTimeString('en-IN', timeOpts)}`

  return `${datePart} · ${timePart}`
}

const facilitatorLinkClass =
  'relative z-20 text-gray-600 underline decoration-gray-300 underline-offset-2 hover:text-primary-700 hover:decoration-primary-400 transition-colors'

const ExplorerEventCard = ({ event, onView }) => {
  const free = isFreeEvent(event)
  const city = getEventCity(event)
  const typeTags = event.eventTypes || []
  const facilitatorNames = getFacilitatorNames(event)
  const [facilitatorsExpanded, setFacilitatorsExpanded] = useState(false)
  const [facilitatorsClamped, setFacilitatorsClamped] = useState(false)
  const facilitatorsRef = useRef(null)

  // Detect whether the two-line clamp actually hides names, so the
  // "show all" toggle only appears when there is something to reveal.
  useLayoutEffect(() => {
    const el = facilitatorsRef.current
    if (el) setFacilitatorsClamped(el.scrollHeight > el.clientHeight + 1)
  }, [event, facilitatorsExpanded])

  return (
    <div className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md transition duration-200 hover:-translate-y-1 hover:shadow-xl">
      {/* Stretched link: keeps the whole card clickable while facilitator
          links sit above it via z-20 (same pattern as EventCard). */}
      <Link
        to={`/events/${event.slug}`}
        onClick={onView}
        className="absolute inset-0 z-10"
        aria-label={event.name}
      />
      {/* Poster well — poster is contained, never cropped */}
      <div className="relative flex h-[260px] items-center justify-center border-b border-primary-100 bg-gradient-to-br from-primary-100 via-cream-50 to-primary-50 p-1">
        <img
          src={getEventImage(event)}
          alt=""
          loading="lazy"
          className="max-h-full max-w-full rounded object-contain shadow-md"
          onError={(e) => {
            e.target.onerror = null
            e.target.src = `${getMediaBaseUrl()}/uploads/events/default-event.jpg`
          }}
        />
        <span className="absolute left-3.5 top-3.5 inline-flex h-[26px] items-center rounded-full bg-white/95 px-3 text-xs font-semibold text-gray-700 shadow-sm">
          {FORMAT_LABELS[event.format] || event.format}
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-2.5 px-[22px] pb-[18px] pt-[22px]">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-primary-500">
          {formatCardDateLine(event)}
        </p>
        <h3 className="font-serif text-xl font-medium leading-snug text-gray-900 line-clamp-2">
          {event.name}
        </h3>
        {event.shortDescription && (
          <p className="text-sm leading-normal text-gray-600 line-clamp-2">
            {event.shortDescription}
          </p>
        )}
        <p className="flex items-center gap-1.5 text-sm text-gray-600">
          <MapPin size={14} strokeWidth={1.75} className="shrink-0 text-gray-400" />
          {getLocationText(event)}
        </p>
        {facilitatorNames.length > 0 && (
          <div className="text-sm text-gray-400">
            <p ref={facilitatorsRef} className={facilitatorsExpanded ? '' : 'line-clamp-2'}>
              with{' '}
              <span className="text-gray-600">
                {renderFacilitatorLinks(event, true, facilitatorLinkClass)}
              </span>
            </p>
            {(facilitatorsClamped || facilitatorsExpanded) && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setFacilitatorsExpanded((v) => !v)
                }}
                className="relative z-20 mt-0.5 text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                {facilitatorsExpanded ? 'Show less' : 'Show all'}
              </button>
            )}
          </div>
        )}
        {(city || typeTags.length > 0) && (
          <div className="mt-auto flex flex-wrap gap-1.5 pt-1.5">
            {city && (
              <span className="inline-flex h-6 items-center gap-1 rounded-full border border-primary-100 bg-primary-50 px-2.5 text-xs font-medium text-primary-700">
                <MapPin size={10} strokeWidth={2} />
                {city}
              </span>
            )}
            {typeTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex h-6 items-center rounded-full border border-primary-100 bg-primary-50 px-2.5 text-xs font-medium text-primary-700"
              >
                {TYPE_LABELS[tag] || tag}
              </span>
            ))}
          </div>
        )}

        {/* Footer rail — the ONLY place price appears */}
        <div className={`flex items-center justify-between gap-3 border-t border-gray-200 pt-3 ${city || typeTags.length > 0 ? 'mt-3' : 'mt-auto'}`}>
          {free ? (
            <span className="inline-flex h-6 items-center rounded-full bg-primary-100 px-3 text-xs font-semibold text-primary-700">
              Free
            </span>
          ) : (
            <span className="text-base font-semibold text-primary-800">{event.fee}</span>
          )}
          <span className="whitespace-nowrap text-sm font-semibold text-primary-600 group-hover:text-primary-700">
            Details →
          </span>
        </div>
      </div>
    </div>
  )
}

export default ExplorerEventCard
